import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../Global/Layout';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { API_CONFIG, ROLE_DISPLAY, getCurrentRole } from '../../../contexts/constants';
import { formatDateTable, formatDateTimeDefault } from '../../../utils/dateFormatter';
import { formatTHB } from '../../../utils/numberFormat';
import Countdown from '../../../utils/Countdown';

const DashboardPage1 = ({ onLogout, userRole }) => {
	const [loading, setLoading] = useState(false);
	const [newsPromotions, setNewsPromotions] = useState([]);
	const [promotionNews, setPromotionNews] = useState('');
	const [powerballData, setPowerballData] = useState(null);
	const [megaMillionsData, setMegaMillionsData] = useState(null);
   	const navigate = useNavigate();
	const currentUserRole = getCurrentRole(userRole);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_CONFIG.BASE_URL}/dashboard/dashboard-data`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success) {
        const { promotionNews, powerball, megaMillions } = response.data.data;

        setPromotionNews(promotionNews || '');
        setPowerballData(powerball);
        setMegaMillionsData(megaMillions);
      }

      const newsParams = new URLSearchParams({
        page: 1,
        pageSize: 6,
        scope: 'newest'
      });

      const newsResponse = await axios.get(`${API_CONFIG.BASE_URL}/news?${newsParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (newsResponse.data && Array.isArray(newsResponse.data.data)) {
        setNewsPromotions(newsResponse.data.data);
      } else {
        setNewsPromotions([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);

      // แสดง error message จาก API
      let errorMessage = 'ไม่สามารถโหลดข้อมูลได้';

      if (error.response) {
        errorMessage = error.response.data?.message || `เกิดข้อผิดพลาด (${error.response.status})`;

        if (error.response.status === 401 || error.response.status === 403) {
          errorMessage = error.response.data?.message || 'กรุณาเข้าสู่ระบบใหม่';
          Swal.fire({
            icon: 'error',
            title: 'เซสชันหมดอายุ',
            text: errorMessage,
            confirmButtonText: 'ตกลง'
          });
          setTimeout(() => {
            localStorage.removeItem('token');
            onLogout();
          }, 1500);
          return;
        }
      } else if (error.request) {
        errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
      }

      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: errorMessage,
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  	if (!['sysadmin', 'customer'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

  return (
    <Layout title={`หน้าแรก ${ROLE_DISPLAY[currentUserRole]}`} onLogout={onLogout} userRole={currentUserRole}>
      <div className="space-y-6">
        {/* ส่วนข่าวสารและโปรโมชั่น */}
        <div className="mb-6">
          <div className="flex justify-between items-center gap-3 mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              📢
            </h2>
            <div className="px-4 py-2 rounded-md shadow-md bg-white dark:bg-surface-dark overflow-hidden">
              <div className="animate-marquee whitespace-nowrap">
                <b>{promotionNews || 'ยินดีต้อนรับสู่ระบบ Lotto Ball'}</b>
              </div>
            </div>
          </div>

          {/* Top grid: Winning Numbers | Winners | Video */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 justify-center gap-4">
            {/* Winning Numbers */}
            <div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark overflow-hidden">
              <div className="px-4 py-3 border-b border-borderc-light dark:border-borderc-dark bg-blue-50 dark:bg-[#2a2a2a] text-black dark:text-white/90">
                <h2 className="text-xl font-semibold text-black dark:text-white text-center">Powerball</h2>
              </div>
              <div className="p-4">
                {/* Jackpot */}
                <div className="text-center mb-6">
                  <div className="text-sm tracking-wide text-gray-500 dark:text-gray-400 uppercase">Jackpot</div>
                  <div className="text-3xl font-extrabold text-red-600 dark:text-red-400 mt-1">
                    {powerballData?.match_price_5_1_bath
                      ? `${formatTHB(powerballData.match_price_5_1_bath)} บาท`
                      : '...'}
                  </div>
                </div>

                {/* Next Drawing */}
                <div className="text-center mb-3">
                  <div className="text-sm tracking-wide text-gray-500 dark:text-gray-400 uppercase">ออกรางวัลครั้งถัดไป</div>
                  <div className="text-3xl font-extrabold text-black dark:text-white mt-1">
                    {powerballData?.prize_announcement_date
                      ? formatDateTable(powerballData.prize_announcement_date)
                      : '...'}
                  </div>
                </div>

                {/* Countdown */}
                {powerballData?.prize_announcement_date && (
                  <Countdown
                    targetDate={powerballData.prize_announcement_date}
					orderActive={powerballData.order_active}
					orderStatus={powerballData.status}
                    type="powerball"
                    orientation="horizontal"
                  />
                )}
              </div>
            </div>

            {/* Winners */}
            <div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark overflow-hidden">
              <div className="px-4 py-3 border-b border-borderc-light dark:border-borderc-dark bg-blue-50 dark:bg-[#2a2a2a] text-black dark:text-white/90">
                <h2 className="text-xl font-semibold text-black dark:text-white text-center">Mega Millions</h2>
              </div>
              <div className="p-4">
                {/* Jackpot */}
                <div className="text-center mb-6">
                  <div className="text-sm tracking-wide text-gray-500 dark:text-gray-400 uppercase">Jackpot</div>
                  <div className="text-3xl font-extrabold text-yellow-500 dark:text-yellow-400 mt-1">
                    {megaMillionsData?.match_price_5_1_bath
                      ? `${formatTHB(megaMillionsData.match_price_5_1_bath)} บาท`
                      : '...'}
                  </div>
                </div>

                {/* Next Drawing */}
                <div className="text-center mb-3">
                  <div className="text-sm tracking-wide text-gray-500 dark:text-gray-400 uppercase">ออกรางวัลครั้งถัดไป</div>
                  <div className="text-3xl font-extrabold text-black dark:text-white mt-1">
                    {megaMillionsData?.prize_announcement_date
                      ? formatDateTable(megaMillionsData.prize_announcement_date)
                      : '...'}
                  </div>
                </div>

                {megaMillionsData?.prize_announcement_date && (
                  <Countdown
                    targetDate={megaMillionsData.prize_announcement_date}
					orderActive={megaMillionsData.order_active}
					orderStatus={megaMillionsData.status}
                    type="megamillions"
                    orientation="horizontal"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <section className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                ข่าวสารและโปรโมชั่น
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                อัปเดตข่าวสารและโปรโมชั่นล่าสุดสำหรับลูกค้า
              </p>
            </div>
            <button
              onClick={() => navigate('/news/views')}
              className="self-start sm:self-auto px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors text-sm font-medium"
            >
              ดูทั้งหมด
            </button>
          </div>

          {loading ? (
            <div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-8 text-center text-gray-500 dark:text-gray-300">
              กำลังโหลดข่าวสาร...
            </div>
          ) : newsPromotions.length === 0 ? (
            <div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-8 text-center text-gray-500 dark:text-gray-400">
              ยังไม่มีข่าวสารและโปรโมชั่น
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {newsPromotions.map((item) => (
                <article
                  key={item.id}
                  className={`bg-white dark:bg-surface-dark border-l-4 rounded-lg shadow-md p-4 transition-all hover:shadow-lg cursor-pointer ${
                    item.priority === 'high'
                      ? 'border-l-red-500'
                      : item.priority === 'medium'
                      ? 'border-l-yellow-500'
                      : 'border-l-blue-500'
                  }`}
                  onClick={() => navigate(`/news/views/${item.id}`)}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        item.type === 'promotion'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}
                    >
                      {item.badge || (item.type === 'promotion' ? 'โปรโมชั่น' : 'ข่าวสาร')}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDateTimeDefault(item.date)}
                    </span>
                  </div>

                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {item.title}
                  </h3>

                  <div
                    className="text-sm text-gray-600 dark:text-gray-300 mb-4"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />

                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                      {item.type === 'promotion' ? (
                        <svg className="w-4 h-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-blue-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span>{item.type === 'promotion' ? 'โปรโมชั่น' : 'ข่าวสาร'}</span>
                    </div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">อ่านเพิ่มเติม</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

export default DashboardPage1;
